'use client'

import { useState, useCallback, useEffect } from 'react'
import { usePostCreationStore } from '@/lib/stores/postCreationStore'
import { RelationshipType, PostRelationship, RelatedPost } from '@/lib/types/posts'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Check, ChevronsUpDown, Link2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs'

type PostSearchResult = {
  id: number
  title: string
  post_type: string
  author_id: string
}

export function PostRelationshipManager() {
  const supabase = createClientComponentClient()
  const { currentPost, updatePostField } = usePostCreationStore()
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState<PostSearchResult[]>([])
  const [selectedPostId, setSelectedPostId] = useState<number | undefined>(undefined)
  const [selectedType, setSelectedType] = useState<RelationshipType | ''>('')
  const [orderInSeries, setOrderInSeries] = useState<number>(0)
  const [loading, setLoading] = useState(false)

  const relationships = currentPost.relationships || []
  
  // Log for debugging
  useEffect(() => {
    console.log('PostRelationshipManager mounted')
    console.log('Current post ID:', currentPost.id)
    console.log('Current relationships:', relationships)
  }, [currentPost, relationships])

  const searchPosts = useCallback(async (query: string) => {
    if (!query) {
      setSearchResults([])
      return
    }

    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('id, title, post_type, author_id')
        .textSearch('title', query)
        .filter('id', 'neq', currentPost.id)
        .filter('is_published', 'eq', true)
        .limit(5)

      if (error) throw error
      
      if (!data || data.length === 0) {
        console.log('No posts found matching the query:', query)
      }
      
      setSearchResults(data || [])
    } catch (error) {
      console.error('Error searching posts:', error)
      setSearchResults([])
    } finally {
      setLoading(false)
    }
  }, [currentPost.id, supabase])

  const handleAddRelationship = async () => {
    if (!selectedPostId || !selectedType) return

    const newRelationship: PostRelationship = {
      id: Date.now(), // Temporary ID until saved to DB
      source_post_id: currentPost.id,
      target_post_id: selectedPostId,
      relationship_type: selectedType as RelationshipType,
      order_in_series: selectedType === RelationshipType.SERIES ? orderInSeries : undefined,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }

    const updatedRelationships = [...relationships, newRelationship]
    updatePostField('relationships', updatedRelationships)

    // Reset form
    setSelectedPostId(undefined)
    setSelectedType('')
    setOrderInSeries(0)
    setSearchValue('')
  }

  const handleRemoveRelationship = (relationshipId: number) => {
    const updatedRelationships = relationships.filter(r => r.id !== relationshipId)
    updatePostField('relationships', updatedRelationships)
  }

  const getRelationshipLabel = (type: RelationshipType) => {
    switch (type) {
      case RelationshipType.SERIES:
        return 'Part of Series'
      case RelationshipType.RELATED:
        return 'Related To'
      case RelationshipType.PREREQUISITE:
        return 'Prerequisite'
      case RelationshipType.CONTRASTING_VIEWPOINT:
        return 'Contrasting Viewpoint'
      case RelationshipType.FOLLOW_UP:
        return 'Follow-up To'
      default:
        return type
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-4">Post Relationships</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Find Post</Label>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between"
                >
                  {selectedPostId
                    ? searchResults.find((post) => post.id === selectedPostId)?.title
                    : "Select a post..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0">
                <Command>
                  <CommandInput
                    placeholder="Search posts..."
                    value={searchValue}
                    onValueChange={(value: string) => {
                      setSearchValue(value)
                      searchPosts(value)
                    }}
                  />
                  <CommandEmpty>
                    {loading ? 'Searching...' : 'No posts found.'}
                  </CommandEmpty>
                  <CommandGroup>
                    <ScrollArea className="h-72">
                      {searchResults.map((post) => (
                        <CommandItem
                          key={post.id}
                          value={post.id.toString()}
                          onSelect={() => {
                            setSelectedPostId(post.id)
                            setOpen(false)
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedPostId === post.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <div className="flex flex-col">
                            <span>{post.title}</span>
                            <span className="text-sm text-muted-foreground">
                              {post.post_type}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </ScrollArea>
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>Relationship Type</Label>
            <Select
              value={selectedType}
              onValueChange={(value) => setSelectedType(value as RelationshipType)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type..." />
              </SelectTrigger>
              <SelectContent>
                {Object.values(RelationshipType).map((type) => (
                  <SelectItem key={type} value={type}>
                    {getRelationshipLabel(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedType === RelationshipType.SERIES && (
            <div className="space-y-2">
              <Label>Order in Series</Label>
              <Input
                type="number"
                min={0}
                value={orderInSeries}
                onChange={(e) => setOrderInSeries(parseInt(e.target.value) || 0)}
                placeholder="Position in series"
              />
            </div>
          )}

          <div className="md:col-span-2">
            <Button
              onClick={handleAddRelationship}
              disabled={!selectedPostId || !selectedType}
              className="w-full"
            >
              <Link2 className="mr-2 h-4 w-4" />
              Add Relationship
            </Button>
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-medium mb-2">Existing Relationships</h3>
        <div className="space-y-2">
          {relationships.length === 0 ? (
            <p className="text-sm text-muted-foreground">No relationships added yet.</p>
          ) : (
            relationships.map((relationship) => {
              const relatedPost = searchResults.find(
                (post) => post.id === relationship.target_post_id
              )
              return (
                <div
                  key={relationship.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      {getRelationshipLabel(relationship.relationship_type)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {relatedPost?.title || `Post #${relationship.target_post_id}`}
                      {relationship.relationship_type === RelationshipType.SERIES &&
                        ` (Order: ${relationship.order_in_series})`}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveRelationship(relationship.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )
            })
          )}
        </div>
        
        {searchResults.length === 0 && searchValue && !loading && (
          <div className="mt-4 p-4 border rounded bg-yellow-50">
            <p className="text-sm">No posts found matching your search. Try a different query or create more posts first.</p>
          </div>
        )}
      </div>
    </div>
  )
} 